import { useEffect } from "react";
import ScottsdaleHeroSection from "./components/HeroSection";
import ScottsdaleStickyNav from "./components/StickyNav";
import ScottsdaleProcessingTimeSection from "./components/ProcessingTimeSection";
import ScottsdaleServicesSection from "./components/ServicesSection";
import ScottsdaleLocalAreaSection from "./components/LocalAreaSection";
import ScottsdaleTravelTimingSection from "./components/TravelTimingSection";
import ScottsdaleDestinationsSection from "./components/DestinationsSection";
import ScottsdaleTestimonialsSection from "./components/TestimonialsSection";
import ScottsdaleAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import ScottsdaleComparisonSection from "./components/ComparisonSection";
import ScottsdaleMistakesSection from "./components/MistakesSection";
import ScottsdaleHowItWorksSection from "./components/HowItWorksSection";
import ScottsdaleFAQSection, { faqs } from "./components/FAQSection";
import ScottsdaleHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import ScottsdaleFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function ScottsdalePage() {
  useEffect(() => {
    document.title = "Emergency Passport Scottsdale, AZ — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/scottsdale/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Scottsdale, AZ — as fast as 24 hours. Reserved courier processing for Mayo Clinic physicians, Barrett-Jackson collectors, resort executives, and Scottsdale residents. A+ BBB rated, registered State Dept. courier. No LA agency drive required.");
    setMeta("geo.placename", "Scottsdale, Arizona");
    setMeta("geo.region", "US-AZ");
    setMeta("geo.position", "33.4942;-111.9261");
    setMeta("ICBM", "33.4942, -111.9261");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Arizona", item: "https://fastpassportcenter.com/arizona/" },
          { "@type": "ListItem", position: 3, name: "Scottsdale Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Scottsdale, AZ residents — as fast as 24 hours. Serving Northeast Phoenix metro including Paradise Valley, Fountain Hills, and Cave Creek.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Scottsdale", containedInPlace: { "@type": "State", name: "Arizona" } },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-scottsdale";
    document.getElementById("structured-data-scottsdale")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data-scottsdale")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <ScottsdaleHeroSection />
      <ScottsdaleStickyNav />
      <ScottsdaleProcessingTimeSection />
      <ScottsdaleServicesSection />
      <ScottsdaleLocalAreaSection />
      <ScottsdaleTravelTimingSection />
      <ScottsdaleDestinationsSection />
      <ScottsdaleTestimonialsSection />
      <ScottsdaleAcceptanceFacilitiesSection />
      <ScottsdaleComparisonSection />
      <ScottsdaleMistakesSection />
      <ScottsdaleHowItWorksSection />
      <ScottsdaleFAQSection />
      <ScottsdaleHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/scottsdale" state="AZ" />
      <ScottsdaleFinalCTA />
    </div>
  );
}